import { ClassListType } from "utils/interfaces";
import AssignClassCard from "./cards/AssignClassCard";
import { useContext, useEffect, useState } from "react";
import { removeItem, singlify } from "utils/utils";
import EmptyViewPopUp from "../EmptyViewPopUp";
import {
  getCollectionRequest,
  updateRecordRequest,
} from "services/apiRequests";
import { LoadingDots } from "utils/loadingAnimation";
import SuccessPopUpMessage from "../SuccessPopUpMessage";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { logoutFunction } from "services/authRequests";

interface ClassesCardsListProps {
  selectedClasses: ClassListType[];
  closeOnceClickOnCard: boolean;
  selectedLesson?: string;
  lessonIdToAssign?: string;
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedClasses: React.Dispatch<React.SetStateAction<ClassListType[]>>;
  showDoneButton: boolean;
}

const ClassesCardsList: React.FC<ClassesCardsListProps> = ({
  selectedClasses,
  closeOnceClickOnCard,
  selectedLesson,
  lessonIdToAssign,
  setShowPopUp,
  setSelectedClasses,
  showDoneButton,
}) => {
  // States definition
  const [classData, setClassData] = useState<ClassListType[]>([]);
  const [selectedClassDataIds, setSelectedClassDataIds] = useState<string[]>(
    []
  );
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentClassName, setCurrentClassName] = useState("");

  // Get the values from the context
  const { classesTerminology } = usePlatform();
  const { contextSchoolByRole } = useContext(MainContext);

  /**
   * Function to load the data
   */
  const getData = async () => {
    // First of all, start the animation
    setIsLoadingData(true);

    // Check if there is contextSchoolByRole (for edition)
    if (!(contextSchoolByRole && contextSchoolByRole._id)) {
      return [];
    }

    // Get request
    const response = await getCollectionRequest(
      `/api/classes?schoolId=${contextSchoolByRole._id}`
    );

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    return data;
  };

  /**
   * Definition of the Add or remove action
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectAction = async (
    classItem: ClassListType,
    action: "add" | "remove"
  ) => {
    // Send the update request if we are on the assign lesson to class
    if (selectedLesson && lessonIdToAssign) {
      // And add or remove the data depending on the case
      const response = await updateRecordRequest(
        {
          classId: classItem._id,
          courseId: lessonIdToAssign,
          action: action,
        },
        "/api/classes/assign-course"
      );

      // Check if the response was successful
      if (!response.successful) {
        alert(response.message);
        return false;
      }
    }

    // Add to the current selected contact data ids
    if (action === "add") {
      // Add to the ids list
      setSelectedClassDataIds([
        ...new Set([...selectedClassDataIds, classItem._id]),
      ]);
      
      // Add to selected classes
      setSelectedClasses(prev => [...prev, classItem]);

      // And close the panel
      if (closeOnceClickOnCard) setShowPopUp(false);
      // Or send the signal to show the pop up message
      else {
        setShowSuccessPopUp(true);
        setCurrentClassName(classItem.name);
      }
    }
    // Or delete from it
    else if (action === "remove") {
      setSelectedClassDataIds(removeItem(selectedClassDataIds, classItem._id));
      setSelectedClasses(prev => prev.filter(c => c._id !== classItem._id));

      // And force the close of the success message
      setShowSuccessPopUp(false);
    }
  };

  /**
   * Load class data when init
   */
  useEffect(() => {
    // Get the data
    getData().then((data) => {
      // Set the data
      setClassData(data);

      // Define the current selected ids
      setSelectedClassDataIds(
        selectedClasses.map((elem: ClassListType) => elem._id)
      );

      // Stop the animation
      setIsLoadingData(false);
    });
  }, []);


  /**
   * Timer for the warning pop up message
   */
  useEffect(() => {
    if (showSuccessPopUp) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        if (setShowSuccessPopUp && setCurrentClassName) {
          setShowSuccessPopUp(false);
          setCurrentClassName("");
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessPopUp]);

  return (
    <>
      <div className="w-full max-h-[70vh] grow shrink justify-start items-start gap-[5px] inline-flex overflow-y-auto pop-up-scrollbar">
        <div className="w-full h-fit self-stretch flex-col justify-start items-start gap-4 inline-flex">
          {classData.length > 0 ? (
            classData.map((item: ClassListType, index: number) => (
              <AssignClassCard
                key={index}
                classItem={item}
                selectedClassDataIds={selectedClassDataIds}
                selectAction={selectAction}
              />
            ))
          ) : !isLoadingData ? (
            <EmptyViewPopUp />
          ) : (
            <div className="w-full h-[50vh] flex justify-center items-center">
              <LoadingDots bgColor="bg-neutral-300" />
            </div>
          )}
        </div>
        <SuccessPopUpMessage
          name={currentClassName}
          nameType={singlify(classesTerminology)}
          showSuccessPopUp={showSuccessPopUp}
          setShowSuccessPopUp={setShowSuccessPopUp}
          dismissAction={() => {}}
          className={`absolute ${
            showDoneButton ? "bottom-16" : "-bottom-3"
          } w-full h-12 p-3 bg-success-500 rounded-2xl justify-start items-center gap-3 inline-flex`}
        />
      </div>
      {showDoneButton && (
        <div
          className="group self-stretch h-11 px-5 py-3 rounded border border-secondary-500 hover:bg-secondary-600 ease-in-out duration-150 inline-flex justify-center items-center cursor-pointer"
          onClick={() => setShowPopUp(false)}
        >
          <div className="justify-start text-secondary-500 group-hover:text-neutral-25 ease-in-out duration-150 text-lg font-semibold font-sans leading-snug select-none">
            Done
          </div>
        </div>
      )}
    </>
  );
};

export default ClassesCardsList;
